import { auth } from '../config';

export class SecurityService {
  
  // Validate user permissions
  static validateUserAccess(userId: string): boolean {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    if (currentUser.uid !== userId) {
      throw new Error('Unauthorized access attempt');
    }
    
    return true;
  }

  // Validate license data before creation
  static validateLicenseData(licenseData: any): boolean {
    const requiredFields = ['userId', 'licenseType', 'price', 'maxSites'];
    const validLicenseTypes = ['social-pro', 'layout-pro', 'ecommerce-pro', 'complete'] as const;
    
    // Check required fields
    for (const field of requiredFields) {
      if (!licenseData[field] && field !== 'price') {
        throw new Error(`Missing required field: ${field}`);
      }
    }
    
    // Validate license type
    if (!validLicenseTypes.includes(licenseData.licenseType)) {
      throw new Error('Invalid license type');
    }
    
    const licenseType = licenseData.licenseType as typeof validLicenseTypes[number];
    
    // Validate price ranges
    const priceRanges = {
      'social-pro': { min: 25, max: 35 },
      'layout-pro': { min: 15, max: 25 },
      'ecommerce-pro': { min: 35, max: 45 },
      'complete': { min: 75, max: 85 }
    } as const;
    
    // const range = priceRanges[licenseType];
    // if (licenseData.price < range.min || licenseData.price > range.max) {
    //   throw new Error('Invalid price for license type');
    // }
    
    // Validate site limits
    const siteLimits = {
      'social-pro': 1,
      'layout-pro': 1,
      'ecommerce-pro': 1,
      'complete': 5
    } as const;
    
    if (licenseData.maxSites !== siteLimits[licenseType]) {
      throw new Error('Invalid site limit for license type');
    }
    
    return true;
  }

  // Sanitize domain input
  static sanitizeDomain(domain: string): string {
    // Remove protocol
    let cleanDomain = domain.replace(/^https?:\/\//, '');
    
    // Remove www
    cleanDomain = cleanDomain.replace(/^www\./, '');
    
    // Remove trailing slash
    cleanDomain = cleanDomain.replace(/\/$/, '');
    
    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(cleanDomain)) {
      throw new Error('Invalid domain format');
    }
    
    return cleanDomain.toLowerCase();
  }

  // Rate limiting for license operations
  private static operationCounts: Map<string, { count: number; timestamp: number }> = new Map();
  
  static checkRateLimit(userId: string, operation: string, limit: number = 10, windowMs: number = 60000): boolean {
    const key = `${userId}:${operation}`;
    const now = Date.now();
    const userOps = this.operationCounts.get(key);
    
    if (!userOps || (now - userOps.timestamp) > windowMs) {
      this.operationCounts.set(key, { count: 1, timestamp: now });
      return true;
    }
    
    if (userOps.count >= limit) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }
    
    userOps.count++;
    return true;
  }

  // Log security events (for monitoring)
  static logSecurityEvent(event: string, userId: string, details?: any): void {
    console.warn(`🔒 Security Event: ${event}`, {
      userId,
      timestamp: new Date().toISOString(),
      details
    });
    
    // In production, you'd send this to a monitoring service
    // like Sentry, LogRocket, or your own logging system
  }
} 